import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);
const API_URL = `${(process.env.REACT_APP_API_BASE || 'http://localhost:5000')}/api/auth`;

// Axios interceptor: only force logout on 401s for protected endpoints.
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      const url = error.config?.url || '';
      const isAuthEndpoint = /\/auth\/(login|register|validate)$/i.test(url);
      const hadAuthHeader = !!error.config?.headers?.Authorization;
      // For login/register/validate we let the caller handle the 401 so UI can show inline errors
      if (!isAuthEndpoint && hadAuthHeader) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        // Fire a custom event so app-level components can choose to navigate without forcing a full page reload
        try { window.dispatchEvent(new CustomEvent('auth:unauthorized')); } catch(_) {}
      }
    }
    return Promise.reject(error);
  }
);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load user from localStorage on mount
  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('token');
      const userData = localStorage.getItem('user');
      
      if (token && userData) {
        try {
          const parsedUser = JSON.parse(userData);
          
          // Validate token with backend (but don't block if it fails)
          try {
            const response = await axios.get(`${API_URL}/validate`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            
            if (response.status === 200) {
              setUser(parsedUser);
            } else {
              // Token is invalid, clear storage
              localStorage.removeItem('token');
              localStorage.removeItem('user');
            }
          } catch (error) {
            // Token validation failed, but we'll still set the user for now
            // The token will be validated on the next API call
            console.warn('Token validation failed, but continuing with stored user:', error);
            setUser(parsedUser);
          }
        } catch (error) {
          console.error('Error parsing user data from localStorage:', error);
          // Clear invalid data
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        }
      }
      setLoading(false); // Set loading to false after initialization
    };

    initializeAuth();
  }, []);

  const login = async (email, password) => {
    setLoading(true);
    // Clear any old tokens/user info before login
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    try {
  const res = await axios.post(`${API_URL}/login`, { email, password }, { withCredentials: true });
      // Validate response shape
      if (!res.data.token || !res.data.user) {
        return { success: false, error: 'Invalid server response.' };
      }
      // Store JWT and user info in localStorage
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      setUser(res.data.user);
      return { success: true };
    } catch (error) {
      console.log('Full login error object:', error);
      let msg = 'Login failed.';
      if (error.response) {
        console.log('Backend response:', error.response);
        msg = error.response.data && error.response.data.message ? error.response.data.message : (error.response.status === 401 ? 'Invalid credentials' : `Server error (${error.response.status})`);
      } else if (error.request) {
        console.log('No response received:', error.request);
        msg = 'No response from server. Check your network.';
      } else {
        console.log('Error setting up request:', error.message);
        msg = 'Request setup error: ' + error.message;
      }
      return { success: false, error: msg };
    } finally {
      setLoading(false);
    }
  };

  const register = async (email, password) => {
    setLoading(true);
    try {
  const res = await axios.post(`${API_URL}/register`, { email, password }, { withCredentials: true });
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      setUser(res.data.user);
      return { success: true };
    } catch (error) {
      let msg = 'Registration failed.';
      if (error.response && error.response.data && error.response.data.message) {
        msg = error.response.data.message;
      }
      return { success: false, error: msg };
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
  setUser(null);
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  axios.post(`${API_URL}/logout`, {}, { withCredentials: true }).catch(()=>{});
    // Remove any other app-specific keys here if needed
    sessionStorage.clear(); // In case any user data is stored here
    // Unregister service worker if present
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then(regs => {
        regs.forEach(reg => reg.unregister());
      });
    }
    window.location.reload(); // Force reload to ensure all state is reset
  };

  // Allow external flows (Firebase) to set auth state after obtaining app JWT
  const setAuthFromServer = (token, userObj) => {
    if (!userObj) return;
    if (token) localStorage.setItem('token', token); // still store for legacy calls
    localStorage.setItem('user', JSON.stringify(userObj));
    setUser(userObj);
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
  setAuthFromServer,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 